import type { RefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { useCommonSelector } from "../redux/commonStore";
import type { MapWrapperType } from "../ToyotaMap";
import { useToyotaMap } from "../ToyotaMap";

type PropsType = {
    lat: number;
    lon: number;
    zoom: number;
    type: "static" | "dynamic";
};

const useCoordinatesMap = (props: PropsType): { mapContainer: RefObject<HTMLDivElement>; mapInitialized: boolean } => {
    const { lat, lon, zoom, type } = props;

    const mapboxToken = useCommonSelector((state) => state.commonSettings.mapboxToken);

    const mapContainer = useRef<HTMLDivElement>(null);
    const mapRef = useRef<InstanceType<MapWrapperType["StaticMap"]> | null>(null);
    const [mapInitialized, setMapInitialized] = useState<boolean>(false);

    // Initialize mapbox map once it is available.
    const mapWrapper = useToyotaMap();
    useEffect(() => {
        if (mapWrapper && mapContainer.current) {
            const initMap = async (): Promise<void> => {
                const commonOptions = {
                    accessToken: mapboxToken,
                    center: { lat, lng: lon },
                    marker: { lat, lng: lon },
                };
                if (type === "static") {
                    mapRef.current = new mapWrapper.StaticMap(mapContainer.current, {
                        ...commonOptions,
                        zoom,
                    });
                } else {
                    mapRef.current = new mapWrapper.DynamicMap(mapContainer.current, {
                        ...commonOptions,
                        zoomControl: true,
                        resizeToBounds: true,
                        // both of these properties need to be set to zoom to a specific level on init,
                        // the "zoom" property doesn't work (GFI does this the same way).
                        fitToBounds: true,
                        maxZoom: zoom,
                    });
                }

                // Set initialized flag true which should hide the preloader and trigger effects below.
                setMapInitialized(true);
            };
            initMap();
        }
    }, [mapWrapper]);

    return { mapContainer, mapInitialized };
};
export default useCoordinatesMap;
